import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { BookUser, Contact, GraduationCap, HeartPulse, Plus, Trash2, UsersRound } from 'lucide-react'
import { useMemo } from 'react'
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormActions, FormSection } from '@/components/forms/FormSection'
import { FormField } from '@/components/forms/FormField'
import { ImageUpload } from '@/components/forms/ImageUpload'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { classesQuery } from '@/features/academic/api'
import { humanize } from '@/utils/format'
import {
  admitStudentSchema,
  emptyGuardian,
  studentProfileSchema,
  type AdmitStudentInput,
  type AdmitStudentOutput,
} from '../schemas'
import { BLOOD_GROUPS, GUARDIAN_RELATIONS } from '../types'

const FIELDS = [
  'admissionNumber',
  'firstName',
  'lastName',
  'gender',
  'dateOfBirth',
  'bloodGroup',
  'religion',
  'nationality',
  'email',
  'phone',
  'address',
  'medicalNotes',
  'photoFileId',
  'guardians',
  'classId',
  'sectionId',
  'rollNumber',
  'admissionDate',
] as const

interface StudentFormProps {
  mode: 'admit' | 'edit'
  defaultValues: AdmitStudentInput
  photoUrl?: string | null
  submitLabel: string
  onSubmit: (values: AdmitStudentOutput) => Promise<unknown>
  onCancel: () => void
}

export function StudentForm({
  mode,
  defaultValues,
  photoUrl,
  submitLabel,
  onSubmit,
  onCancel,
}: StudentFormProps) {
  const { t } = useTranslation('students')
  const withGuardians = useGuardians()
  const schema =
    mode === 'admit'
      ? admitStudentSchema
      : studentProfileSchema.extend({
          classId: admitStudentSchema.shape.classId.optional(),
          sectionId: admitStudentSchema.shape.sectionId.optional(),
          rollNumber: admitStudentSchema.shape.rollNumber.optional(),
          admissionDate: admitStudentSchema.shape.admissionDate.optional(),
        })
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<AdmitStudentInput, unknown, AdmitStudentOutput>({
    resolver: zodResolver(schema) as unknown as Resolver<AdmitStudentInput, unknown, AdmitStudentOutput>,
    defaultValues,
  })
  const guardians = useFieldArray({ control, name: 'guardians' })
  const classes = useQuery({ ...classesQuery(), enabled: mode === 'admit' })
  const classId = watch('classId')
  const firstName = watch('firstName')
  const lastName = watch('lastName')

  const sections = useMemo(
    () => classes.data?.find((c) => String(c.id) === classId)?.sections ?? [],
    [classes.data, classId],
  )

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <form onSubmit={submit} noValidate className="space-y-6">
      {mode === 'admit' ? (
        <FormSection
          title={t('Placement')}
          description={t('Where the student will study in the current academic year.')}
          icon={<GraduationCap />}
          columns={3}
        >
          <FormField label={t('Class')} error={errors.classId?.message} required>
            <Select
              placeholder={t('Choose a class')}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              {...register('classId', { onChange: () => setValue('sectionId', '') })}
            />
          </FormField>
          <FormField
            label={t('Section')}
            error={errors.sectionId?.message}
            required
            hint={classId && !sections.length ? t('This class has no sections yet.') : undefined}
          >
            <Select
              placeholder={classId ? t('Choose a section') : t('Choose a class first')}
              disabled={!classId}
              options={sections.map((s) => ({
                value: s.id,
                label: `${s.name ? t('Section {{name}}', { name: s.name }) : t('Whole class')}${s.capacity ? ` · ${s.studentCount}/${s.capacity}` : ''}`,
                disabled: s.capacity !== null && s.studentCount >= s.capacity,
              }))}
              {...register('sectionId')}
            />
          </FormField>
          <FormField label={t('Roll number')} error={errors.rollNumber?.message}>
            <Input placeholder={t('e.g. 12')} {...register('rollNumber')} />
          </FormField>
          <FormField label={t('Admission date')} error={errors.admissionDate?.message} required>
            <Input type="date" {...register('admissionDate')} />
          </FormField>
        </FormSection>
      ) : null}

      <FormSection title={t('Personal details')} icon={<BookUser />} columns={3} aside={null}>
        <div className="sm:col-span-2 lg:col-span-3">
          <Controller
            control={control}
            name="photoFileId"
            render={({ field }) => (
              <ImageUpload
                name={`${firstName} ${lastName}`.trim()}
                currentUrl={photoUrl ?? null}
                onUploaded={(id) => field.onChange(id)}
              />
            )}
          />
        </div>
        <FormField label={t('First name')} error={errors.firstName?.message} required>
          <Input autoComplete="off" {...register('firstName')} />
        </FormField>
        <FormField label={t('Last name')} error={errors.lastName?.message} required>
          <Input autoComplete="off" {...register('lastName')} />
        </FormField>
        <FormField label={t('Admission number')} error={errors.admissionNumber?.message} required>
          <Input placeholder="ADM-0001" className="font-mono" {...register('admissionNumber')} />
        </FormField>
        <FormField label={t('Gender')} error={errors.gender?.message} required>
          <Select
            placeholder={t('Choose')}
            options={['female', 'male', 'other'].map((g) => ({ value: g, label: humanize(g) }))}
            {...register('gender')}
          />
        </FormField>
        <FormField label={t('Date of birth')} error={errors.dateOfBirth?.message}>
          <Input type="date" {...register('dateOfBirth')} />
        </FormField>
        <FormField label={t('Blood group')} error={errors.bloodGroup?.message}>
          <Select
            placeholder={t('Unknown')}
            options={BLOOD_GROUPS.map((b) => ({ value: b, label: b }))}
            {...register('bloodGroup')}
          />
        </FormField>
        <FormField label={t('Nationality')} error={errors.nationality?.message}>
          <Input {...register('nationality')} />
        </FormField>
        <FormField label={t('Religion')} error={errors.religion?.message}>
          <Input {...register('religion')} />
        </FormField>
      </FormSection>

      <FormSection title={t('Contact')} icon={<Contact />}>
        <FormField label={t('Email')} error={errors.email?.message}>
          <Input type="email" {...register('email')} />
        </FormField>
        <FormField label={t('Phone')} error={errors.phone?.message}>
          <Input type="tel" {...register('phone')} />
        </FormField>
        <FormField label={t('Home address')} error={errors.address?.message} className="sm:col-span-2">
          <Textarea rows={2} {...register('address')} />
        </FormField>
      </FormSection>

      {withGuardians ? (
        <FormSection
          title={t('Guardians')}
          description={t('Parents or guardians the school may contact. Mark one as primary.')}
          icon={<UsersRound />}
          columns={1}
          aside={
            guardians.fields.length < 4 ? (
              <Button
                variant="soft"
                size="sm"
                leftIcon={<Plus />}
                onClick={() =>
                  guardians.append({
                    ...emptyGuardian,
                    relation: guardians.fields.length ? 'mother' : 'father',
                    isPrimary: guardians.fields.length === 0,
                  })
                }
              >
                {t('Add guardian')}
              </Button>
            ) : null
          }
        >
          {guardians.fields.length === 0 ? (
            <p className="text-sm text-muted">{t('No guardians added.')}</p>
          ) : null}
          {guardians.fields.map((field, index) => {
            const e = errors.guardians?.[index]
            return (
              <fieldset key={field.id} className="rounded-control border border-line bg-surface-muted/50 p-4">
                <legend className="sr-only">{t('Guardian {{number}}', { number: index + 1 })}</legend>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-ink">
                    {t('Guardian {{number}}', { number: index + 1 })}
                  </span>
                  <div className="flex items-center gap-2">
                    <Controller
                      control={control}
                      name={`guardians.${index}.isPrimary`}
                      render={({ field: f }) =>
                        f.value ? (
                          <Badge tone="brand" size="sm">
                            {t('Primary contact')}
                          </Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              guardians.fields.forEach((_, i) =>
                                setValue(`guardians.${i}.isPrimary`, i === index, { shouldDirty: true }),
                              )
                            }
                          >
                            {t('Make primary')}
                          </Button>
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('Remove guardian {{number}}', { number: index + 1 })}
                      onClick={() => guardians.remove(index)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField label={t('Full name')} error={e?.name?.message} required>
                    <Input {...register(`guardians.${index}.name`)} />
                  </FormField>
                  <FormField label={t('Relation')} error={e?.relation?.message} required>
                    <Select
                      options={GUARDIAN_RELATIONS.map((r) => ({ value: r, label: humanize(r) }))}
                      {...register(`guardians.${index}.relation`)}
                    />
                  </FormField>
                  <FormField label={t('Phone')} error={e?.phone?.message}>
                    <Input type="tel" {...register(`guardians.${index}.phone`)} />
                  </FormField>
                  <FormField label={t('Email')} error={e?.email?.message}>
                    <Input type="email" {...register(`guardians.${index}.email`)} />
                  </FormField>
                  <FormField label={t('Occupation')} error={e?.occupation?.message}>
                    <Input {...register(`guardians.${index}.occupation`)} />
                  </FormField>
                </div>
              </fieldset>
            )
          })}
        </FormSection>
      ) : null}

      <FormSection
        title={t('Health')}
        description={t('Allergies, conditions or anything staff should know.')}
        icon={<HeartPulse />}
        columns={1}
      >
        <FormField label={t('Medical notes')} error={errors.medicalNotes?.message}>
          <Textarea rows={3} {...register('medicalNotes')} />
        </FormField>
      </FormSection>

      <FormActions>
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          {t('Cancel')}
        </Button>
        <Button type="submit" loading={isSubmitting} disabled={mode === 'edit' && !isDirty}>
          {submitLabel}
        </Button>
      </FormActions>
    </form>
  )
}
